// ── Session expiry: when to warn, and who needs to save first ─────────────────
// Okta sessions expire, which is correct. What was wrong is that they expired
// SILENTLY and mid-edit: the app noticed on its next poll, dropped straight to
// the sign-in screen, and unmounted the editor with the writer's unsaved work
// inside it (ENG-54731). Reauthenticating is expected; losing an hour of writing
// to it is not.
//
// The timing rules live here, pure and testable, because they are the part that
// is easy to get subtly wrong and impossible to eyeball: a warning that fires
// late is no warning, and one that fires on a session with no expiry at all
// (off-Okta local dev) is a permanent banner nobody can dismiss.

/** How long before expiry the warning appears. The ticket asks for five minutes. */
export const WARN_BEFORE_MS = 5 * 60_000;

/**
 * How often the app re-checks. Coarse while there is nothing to say, per-second
 * once the countdown is on screen — a warning that reads "4 minutes" for a whole
 * minute and then jumps looks broken, and this is the one moment the user is
 * being asked to trust the number.
 */
export const IDLE_POLL_MS = 30_000;
export const WARNING_POLL_MS = 1_000;

/**
 * How long before expiry the open work is saved, WITHOUT waiting for the user.
 *
 * Before, not at or after. The criterion is "saved to draft state before the
 * session ends", and that is not a nicety of wording: a save issued once the
 * token has expired goes out with no valid credential and comes back 401, so
 * flushing on expiry is flushing into a closed door. Thirty seconds is enough for
 * a draft PUT to complete on a bad connection and short enough that the saved
 * body is what the writer last typed.
 */
export const FLUSH_BEFORE_MS = 30_000;

export type SessionPhase = "none" | "ok" | "warning" | "expired";

/**
 * Which phase a session is in.
 *
 * `none` is its own answer, not a variant of `expired`: off-Okta there is no
 * session to expire, and treating "no expiry" as "expired" would show every
 * local-dev user a countdown to nothing. `expiresAt` of null means exactly that.
 */
export function sessionPhase(
  expiresAt: number | null,
  now: number,
  warnBeforeMs: number = WARN_BEFORE_MS,
): SessionPhase {
  if (expiresAt === null) return "none";
  const left = expiresAt - now;
  if (left <= 0) return "expired";
  return left <= warnBeforeMs ? "warning" : "ok";
}

/** Milliseconds left, floored at zero. Null when there is no session. */
export function msUntilExpiry(expiresAt: number | null, now: number): number | null {
  if (expiresAt === null) return null;
  return Math.max(0, expiresAt - now);
}

/**
 * The countdown, as a person reads a clock: "4:07", "0:09".
 *
 * Seconds are shown throughout rather than switching to "less than a minute" at
 * the end — under a minute is exactly when the number matters most, and a vague
 * phrase there reads as the app not knowing either.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/**
 * Whether it is time to save the open work — the last moment at which a save can
 * still succeed.
 *
 * Deliberately not derived from the phase: "warning" spans five minutes, and
 * saving at the start of it would persist a body the writer then keeps editing
 * for four more minutes, so the saved draft would be the stale one. Nor is it the
 * same instant as expiry, for the 401 reason above.
 */
export function shouldFlushNow(msLeft: number | null, flushBeforeMs: number = FLUSH_BEFORE_MS): boolean {
  return msLeft !== null && msLeft > 0 && msLeft <= flushBeforeMs;
}

/** How often to tick, given the phase. */
export function pollIntervalFor(phase: SessionPhase): number {
  return phase === "warning" ? WARNING_POLL_MS : IDLE_POLL_MS;
}

/**
 * The `exp` claim of a JWT, in epoch MILLISECONDS, or null when it can't be read.
 *
 * NOT a validity check and never a trust boundary — the signature is not
 * examined here, and the API verifies every token against Okta's JWKS on every
 * request (core/auth.ts). This exists so the countdown counts down to the moment
 * the token the app actually authenticates with dies.
 *
 * Which is a different moment from the one the session was being measured by.
 * `expires_in` from the token endpoint describes the ACCESS token, and the app
 * sends the ID TOKEN as its Bearer. With no custom-API scope requested
 * (`openid profile email groups offline_access`), those are two independently
 * configured lifetimes on the org authorization server — ID tokens pinned by the
 * AS, the access token by an access-policy rule — and they can diverge in either
 * direction. Only the ID token's `exp` decides whether a request is accepted, so
 * a warning scheduled off `expires_in` fires at the wrong time, and if the ID
 * token is the shorter of the two the user is signed out before being warned at
 * all — the very failure the warning exists to prevent.
 *
 * Returning null here means "couldn't read it", NOT "no session": callers must
 * fall back to `expires_in` rather than passing the null on to sessionPhase,
 * which reads null as phase `none` and would switch the whole warning off.
 *
 * Tolerates the base64url alphabet and missing padding (a JWT carries neither
 * `+`/`/` nor `=`). A non-numeric or absent `exp` is null, as is anything that
 * isn't a decodable JWT.
 */
export function jwtExpiresAt(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof claims.exp === "number" && Number.isFinite(claims.exp) ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

// ── Flushing work before the session ends ────────────────────────────────────
// A screen holding unsaved work registers a flush, exactly as one holding unsaved
// work registers a nav blocker (router.ts's registerNavBlocker — same shape, same
// reason). The alternative is AuthGate knowing about the page editor, which
// inverts the dependency and would have to learn about every future surface that
// can hold work.
//
// A flush runs while the token is STILL VALID. That ordering is the whole point:
// after expiry the save would 401, and a save that can't happen is not a save.

/** Returns true when the work is safely persisted (or there was none). */
type SessionFlush = () => boolean | Promise<boolean>;
const flushes = new Set<SessionFlush>();

export function registerSessionFlush(fn: SessionFlush): () => void {
  flushes.add(fn);
  return () => {
    flushes.delete(fn);
  };
}

/**
 * Run every registered flush, and report whether ALL of them persisted.
 *
 * Every one runs even if an earlier fails — two editors open must not have one's
 * failure abandon the other's work — and a throwing flush counts as a failure
 * rather than taking the others down with it. The caller uses the result to
 * decide what to TELL the user, never to decide whether to reauthenticate: the
 * session is ending either way, and the honest thing is to say what didn't save.
 */
export async function flushBeforeExpiry(): Promise<boolean> {
  if (flushes.size === 0) return true;
  const results = await Promise.all(
    [...flushes].map(async (fn) => {
      try {
        return await fn();
      } catch {
        return false;
      }
    }),
  );
  return results.every(Boolean);
}

/** Test seam — a registration leaked from one case must not affect the next. */
export function resetSessionFlushes(): void {
  flushes.clear();
}

/** How many surfaces currently hold work. Lets the warning say "your changes to
 *  this page will be saved" only when that is actually true. */
export function pendingFlushCount(): number {
  return flushes.size;
}
